// Estatística do indicador de Lead Time de Fatura (PO1-2422).
//
// Funções puras, sem React e sem chamada de API: a tela só orquestra a busca e
// entrega as listas para cá. Ficam separadas do componente para serem testáveis
// (ver __tests__/estatisticas.test.js) — o cálculo é a parte do indicador que
// não pode errar em silêncio.

import moment from "moment";

import { SITUACOES_FATURA } from "../../ConferenciaFatura/formatters";

// Ciclo da fatura = da emissão pela transportadora até a integração no ERP
// (situação PAG=8, exposta na Auditoria de Faturas como "Dt. Integração" /
// campo dataPagamento). Não é a data do pagamento bancário.
export const SITUACAO_INTEGRADA = 8;

// Faturas ainda na esteira (backlog): AVA, EVA, VAL, LIB, BLO, CON e, desde a
// PO1-3191, EAN (9, Em Análise) e ETR (10, Em Tratativa). Fora ficam as
// terminais — Integrada (8), Paga (11, PO1-3653) e Cancelada (3).
export const SITUACOES_EM_CURSO = [1, 2, 6, 7, 4, 5, 9, 10];

// Os mesmos estados pelo código que o /faturas/conferencia/resumo devolve.
// Derivado de SITUACOES_FATURA para não divergir do resto do Hub.
export const CODIGOS_EM_CURSO = SITUACOES_EM_CURSO.map((value) => {
  const situacao = SITUACOES_FATURA.find((s) => s.value === value);
  return situacao && situacao.codigo;
}).filter(Boolean);

export const META_PADRAO_DIAS = 10;

// Tons das faixas: success → info → accent → pink. O tom do meio é `accent`
// (roxo) e não `warning` porque no tema escuro --everlog-status-warning (salmão)
// e --everlog-status-danger (rosa) ficam quase iguais em linhas vizinhas. A
// mesma escala vale para o Aging por situação (PO1-3001): as duas visões de
// envelhecimento da tela pintam as faixas do mesmo jeito.
export const FAIXAS_AGING = [
  { key: "0-7", label: "Até 7 dias", min: 0, max: 7, tone: "success" },
  { key: "8-15", label: "8 a 15 dias", min: 8, max: 15, tone: "info" },
  { key: "16-30", label: "16 a 30 dias", min: 16, max: 30, tone: "accent" },
  { key: "30+", label: "Mais de 30 dias", min: 31, max: Infinity, tone: "pink" },
];

// Emissão da fatura: a data externa (enviada pela transportadora) é a
// referência; sem ela, cai para a entrada no FLOW — mesmo fallback da coluna
// "Dt. Emissão" da Auditoria de Faturas.
export const dataEmissao = (f) =>
  (f && (f.dataEmissaoExterna || f.dataEmissaoInterna)) || null;

// Diferença em dias inteiros entre duas datas, ignorando a hora. Devolve null
// quando falta uma das pontas, quando a data não é válida ou quando o intervalo
// é negativo (dado inconsistente não vira estatística).
export const diffDias = (inicio, fim) => {
  if (!inicio || !fim) return null;
  const a = moment(inicio);
  const b = moment(fim);
  if (!a.isValid() || !b.isValid()) return null;
  const dias = b.clone().startOf("day").diff(a.clone().startOf("day"), "days");
  return dias < 0 ? null : dias;
};

export const media = (valores) =>
  valores && valores.length
    ? valores.reduce((acc, v) => acc + v, 0) / valores.length
    : null;

// Recebe a lista JÁ ORDENADA (crescente).
export const mediana = (ordenados) => {
  if (!ordenados || !ordenados.length) return null;
  const meio = Math.floor(ordenados.length / 2);
  return ordenados.length % 2
    ? ordenados[meio]
    : (ordenados[meio - 1] + ordenados[meio]) / 2;
};

// Percentil pelo método do "nearest rank" sobre a lista já ordenada.
export const percentil = (ordenados, p) => {
  if (!ordenados || !ordenados.length) return null;
  const idx = Math.ceil((p / 100) * ordenados.length) - 1;
  return ordenados[Math.min(ordenados.length - 1, Math.max(0, idx))];
};

// Faixa de envelhecimento de uma idade em dias. Fora do domínio (null, NaN,
// negativo) devolve undefined — quem chama decide o que fazer.
export const classificarFaixa = (idade) => {
  if (idade === null || idade === undefined || !Number.isFinite(idade)) {
    return undefined;
  }
  return FAIXAS_AGING.find((f) => idade >= f.min && idade <= f.max);
};

// Faturas integradas no período que têm as duas pontas do ciclo. As sem data
// ficam de fora da estatística (a tela reporta quantas foram) — não dá para
// medir o que não tem começo ou fim.
export const medirCiclos = (concluidas) =>
  (concluidas || [])
    .map((f) => ({ ...f, leadTime: diffDias(dataEmissao(f), f.dataPagamento) }))
    .filter((f) => f.leadTime !== null);

// Soma as contagens do /faturas/conferencia/resumo para as situações em curso.
// Código ausente vale 0.
export const somarEmCurso = (counts) =>
  CODIGOS_EM_CURSO.reduce(
    (soma, codigo) => soma + (Number(counts && counts[codigo]) || 0),
    0
  );

// Estatística dos ciclos fechados. `registros` são os itens já medidos por
// medirCiclos (têm `leadTime`). `meta` é a meta de dias configurada na tela.
export const calcularAnalise = (registros, meta) => {
  const lista = registros || [];
  const valores = lista.map((r) => r.leadTime).sort((a, b) => a - b);
  const comDiv = lista.filter((r) => Boolean(r.divergencia));
  const semDiv = lista.filter((r) => !r.divergencia);
  const mediaComDiv = media(comDiv.map((r) => r.leadTime));
  const mediaSemDiv = media(semDiv.map((r) => r.leadTime));

  // Ranking por MATRIZ, não por filial (PO1-3001). O backend resolve a matriz
  // pelo de-para ativo do embarcador e devolve em `transportadoraMatrizFantasia`;
  // sem de-para ele repete a própria transportadora. Agrupar por filial
  // espalhava o mesmo grupo econômico em várias linhas — na Tegma, JF Navegação
  // e Chibatão apareciam separadas sendo a mesma matriz.
  const porMatriz = new Map();
  lista.forEach((r) => {
    const nome =
      r.transportadoraMatrizFantasia || r.transportadoraFantasia || "—";
    const atual = porMatriz.get(nome) || { nome, soma: 0, qtd: 0, valor: 0 };
    atual.soma += r.leadTime;
    atual.qtd += 1;
    atual.valor += Number(r.valTotalFatura) || 0;
    porMatriz.set(nome, atual);
  });

  return {
    qtd: lista.length,
    cicloMedio: media(valores),
    mediana: mediana(valores),
    p90: percentil(valores, 90),
    dentroDaMeta: valores.length
      ? (valores.filter((v) => v <= meta).length / valores.length) * 100
      : null,
    mediaComDiv,
    mediaSemDiv,
    // Quanto a divergência custa em dias: ciclo das faturas com divergência
    // menos o das sem. Isola onde o tempo é perdido.
    custoDaDivergencia:
      mediaComDiv !== null && mediaSemDiv !== null
        ? mediaComDiv - mediaSemDiv
        : null,
    qtdComDiv: comDiv.length,
    valorTotal: lista.reduce(
      (acc, r) => acc + (Number(r.valTotalFatura) || 0),
      0
    ),
    ranking: Array.from(porMatriz.values())
      .map((t) => ({ ...t, ciclo: t.soma / t.qtd }))
      .sort((a, b) => b.ciclo - a.ciclo)
      .slice(0, 10),
  };
};

// Envelhecimento do backlog: onde o dinheiro está parado hoje. `hoje` entra
// como string YYYY-MM-DD para a função ser determinística (e testável).
export const calcularAging = (emCurso, meta, hoje) => {
  const lista = emCurso || [];
  const faixas = FAIXAS_AGING.map((f) => ({ ...f, qtd: 0, valor: 0 }));
  let semData = 0;
  let valorTotal = 0;
  let idades = [];

  lista.forEach((f) => {
    const valor = Number(f.valTotalFatura) || 0;
    valorTotal += valor;
    const idade = diffDias(dataEmissao(f), hoje);
    if (idade === null) {
      semData += 1;
      return;
    }
    idades.push(idade);
    const faixa = faixas.find((x) => idade >= x.min && idade <= x.max);
    if (faixa) {
      faixa.qtd += 1;
      faixa.valor += valor;
    }
  });

  idades = idades.sort((a, b) => a - b);
  return {
    faixas,
    semData,
    total: lista.length,
    valorTotal,
    idadeMedia: media(idades),
    acimaDaMeta: idades.filter((i) => i > meta).length,
  };
};
